import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import SEO from "@/components/SEO";
import { Download, LogIn, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "verde2050.projectAdminToken";

export default function ProjectAdmin() {
    const [token, setToken] = useState(() => (typeof window !== "undefined" ? window.localStorage.getItem(TOKEN_KEY) || "" : ""));
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState("");

    const authed = !!token;

    const login = async (e) => {
        e?.preventDefault();
        setLoginError("");
        try {
            const r = await axios.post(`${API}/admin/login`, { password });
            window.localStorage.setItem(TOKEN_KEY, r.data.token);
            setToken(r.data.token);
            setPassword("");
        } catch {
            setLoginError("Incorrect password.");
        }
    };
    const logout = () => {
        window.localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setLeads([]);
        setStats(null);
    };

    const fetchAll = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const headers = { "X-Admin-Password": token };
            const [leadsRes, statsRes] = await Promise.all([
                axios.get(`${API}/admin/scan-leads`, { headers }),
                axios.get(`${API}/admin/scan-stats`, { headers }),
            ]);
            setLeads(leadsRes.data || []);
            setStats(statsRes.data || null);
        } catch (e) {
            if (e?.response?.status === 401) logout();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

    const downloadCsv = async () => {
        const r = await axios.get(`${API}/admin/scan-leads.csv`, {
            headers: { "X-Admin-Password": token },
            responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement("a");
        a.href = url;
        const ts = new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-");
        a.download = `scan-leads-${ts}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const filtered = useMemo(() => {
        if (!filter.trim()) return leads;
        const q = filter.toLowerCase();
        return leads.filter((l) => (
            (l.name || "").toLowerCase().includes(q) ||
            (l.email || "").toLowerCase().includes(q) ||
            (l.plot_location || "").toLowerCase().includes(q) ||
            (l.notes || "").toLowerCase().includes(q)
        ));
    }, [leads, filter]);

    return (
        <div data-testid="project-admin" className="bg-cream text-ink min-h-screen">
            <SEO path="/project-admin" title="Project Admin — Verde 2050" description="Internal." noindex />

            <header className="border-b border-hairline bg-cream">
                <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <p className="mono-label tracking-[0.18em] text-forest">VERDE 2050 · PROJECT · ADMIN</p>
                    {authed && (
                        <div className="flex items-center gap-4 mono-label text-muted_text">
                            <span>{stats ? `${stats.total} total · ${stats.today} today` : "—"}</span>
                            <button onClick={fetchAll} className="hover:text-forest transition-colors inline-flex items-center gap-1" data-testid="admin-refresh">
                                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                            </button>
                            <button onClick={logout} className="hover:text-terracotta transition-colors" data-testid="admin-logout">Sign out</button>
                        </div>
                    )}
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 md:px-10 py-12">
                {!authed ? (
                    <form onSubmit={login} className="max-w-sm border border-hairline bg-surface p-8" data-testid="admin-login">
                        <p className="mono-label text-terracotta mb-3">Sign in</p>
                        <h1 className="font-serif text-3xl text-forest leading-tight mb-6">Project Admin</h1>
                        <input
                            type="password"
                            placeholder="Admin password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="editorial-input w-full mb-3"
                            data-testid="admin-password"
                            autoFocus
                        />
                        {loginError && <p className="mono-label text-terracotta mb-3" data-testid="admin-login-error">{loginError}</p>}
                        <button type="submit" className="inline-flex items-center gap-2 bg-forest text-cream hover:bg-terracotta px-5 py-3 mono-label transition-colors" data-testid="admin-login-submit">
                            <LogIn className="h-4 w-4" /> Sign in
                        </button>
                    </form>
                ) : (
                    <>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                            <h1 className="font-serif text-3xl text-forest leading-tight">Project-check submissions</h1>
                            <div className="flex items-center gap-3">
                                <input
                                    type="text"
                                    placeholder="Filter by name, email, region…"
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    className="editorial-input w-72"
                                    data-testid="admin-filter"
                                />
                                <button onClick={downloadCsv} className="inline-flex items-center gap-2 border border-forest text-forest hover:bg-forest hover:text-cream px-5 py-3 mono-label transition-colors" data-testid="admin-csv">
                                    <Download className="h-4 w-4" /> Export CSV
                                </button>
                            </div>
                        </div>

                        <div className="border border-hairline overflow-x-auto bg-cream">
                            <table className="w-full text-sm" data-testid="admin-table">
                                <thead className="bg-surface mono-label text-muted_text text-left">
                                    <tr>
                                        <th className="px-4 py-3">When</th>
                                        <th className="px-4 py-3">Name</th>
                                        <th className="px-4 py-3">Email</th>
                                        <th className="px-4 py-3">Plot</th>
                                        <th className="px-4 py-3">Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 ? (
                                        <tr><td colSpan={5} className="px-4 py-10 text-center text-muted_text">No enquiries yet.</td></tr>
                                    ) : filtered.map((l) => (
                                        <tr key={l.id} className="border-t border-hairline align-top">
                                            <td className="px-4 py-3 mono-label text-muted_text whitespace-nowrap">{formatTs(l.created_at)}</td>
                                            <td className="px-4 py-3 text-forest font-medium">{l.name}</td>
                                            <td className="px-4 py-3"><a href={`mailto:${l.email}`} className="text-forest hover:text-terracotta border-b border-forest/30 hover:border-terracotta">{l.email}</a></td>
                                            <td className="px-4 py-3 max-w-md break-words">{linkifyPlot(l.plot_location)}</td>
                                            <td className="px-4 py-3 max-w-md text-muted_text">{l.notes || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}

function formatTs(ts) {
    if (!ts) return "—";
    try {
        const d = new Date(ts);
        return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch {
        return String(ts).slice(0, 16);
    }
}

function linkifyPlot(text) {
    if (!text) return "—";
    const match = String(text).match(/(https?:\/\/[^\s]+)/i);
    if (match) {
        return (
            <a href={match[1]} target="_blank" rel="noreferrer" className="text-forest hover:text-terracotta border-b border-forest/30 hover:border-terracotta break-all">
                {text}
            </a>
        );
    }
    return text;
}
